import i18n from '@/i18n';
/**
 * Unified API Client
 *
 * Combines Axios with enhanced error handling, type safety, and token refresh logic.
 * This is the single source of truth for all API calls in the application.
 */

import {
  askVisitConfirm,
  askVisitReason,
  isVisitConfirmCode,
  isVisitReasonCode,
  VISIT_CONFIRM_OVERRIDES,
  VISIT_REASON_FIELDS,
  type VisitRefusal,
} from '@/lib/visitConfirm';
import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { Sentry } from '@/lib/sentry';
import { clearSessionStorage } from '@/lib/session';
import { getClientId } from '@/lib/clientId';
import {
  claimIdempotencyKey,
  isMoneyRoute,
  releaseIdempotencyKey,
} from '@/lib/idempotencyKey';

// ============================================================================
// Configuration
// ============================================================================

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

// Public pages that don't require authentication
const PUBLIC_PAGES = [
  '/login',
  '/reset-password',
  '/forgot-password',
  '/signup',
  // A patient's consent signing link: no account, never sent to /login.
  '/consent/sign',
  // A patient's treatment plan review link, the same.
  '/plan/review',
  // A patient's medical history link, the same.
  '/medical-form',
];

// ============================================================================
// Error Types
// ============================================================================

export interface ApiErrorResponse {
  message: string;
  code?: string;
  statusCode: number;
  errors?: Record<string, string[]>;
}

export class ApiError extends Error {
  public readonly statusCode: number;
  public readonly code?: string;
  public readonly errors?: Record<string, string[]>;
  public readonly isApiError = true;

  constructor(response: ApiErrorResponse) {
    super(response.message);
    this.name = 'ApiError';
    this.statusCode = response.statusCode;
    this.code = response.code;
    this.errors = response.errors;
  }

  isValidationError(): boolean {
    return this.statusCode === 400;
  }

  isAuthError(): boolean {
    return this.statusCode === 401;
  }

  isForbiddenError(): boolean {
    return this.statusCode === 403;
  }

  isNotFoundError(): boolean {
    return this.statusCode === 404;
  }

  isServerError(): boolean {
    return this.statusCode >= 500;
  }

  getFieldErrors(): Record<string, string[]> {
    return this.errors || {};
  }
}

// ============================================================================
// Axios Instance
// ============================================================================

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// ============================================================================
// Sentry Observability Interceptors
// ============================================================================

/** A request config that claimed an idempotency key on the way out. */
type IdempotentConfig = { idempotencySignature?: string };

apiClient.interceptors.request.use((config) => {
  // The client defaults to application/json, which is wrong for uploads: a
  // multipart body needs a `boundary` parameter that only the browser can
  // generate. Sending FormData as JSON means the server parses no file at all
  // and answers "No file was uploaded". Deleting the header lets axios set
  // both the type and the boundary from the body itself.
  // Also covers call sites that set `multipart/form-data` by hand: that value
  // is missing the boundary, so it is no more usable than the JSON default.
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }

  // Lets the realtime feed tell this tab which changes were its own. See
  // `lib/clientId.ts` and `hooks/useRealtimeInvalidation.ts`.
  config.headers['X-Client-Id'] = getClientId();

  // A request that moves money carries a key the server dedupes on, so a
  // second click or a retry is answered with the first result instead of
  // paying twice. See `lib/idempotencyKey.ts`. A caller that set its own
  // key keeps it.
  if (
    config.url &&
    isMoneyRoute(config.method, config.url) &&
    !config.headers['X-Idempotency-Key']
  ) {
    const claim = claimIdempotencyKey(
      config.method ?? 'post',
      config.url,
      config.data,
    );
    config.headers['X-Idempotency-Key'] = claim.key;
    (config as IdempotentConfig).idempotencySignature = claim.signature;
  }

  Sentry.addBreadcrumb({
    category: 'http',
    message: `${config.method?.toUpperCase()} ${config.url}`,
    level: 'info',
    data: { url: config.url, method: config.method },
  });
  return config;
});

apiClient.interceptors.response.use(
  (response) => {
    const claimed = (response.config as IdempotentConfig).idempotencySignature;
    if (claimed) releaseIdempotencyKey(claimed, true);
    Sentry.addBreadcrumb({
      category: 'http',
      message: `${response.status} ${response.config.url}`,
      level: 'info',
      data: { status: response.status, url: response.config.url },
    });
    return response;
  },
  (error: AxiosError) => {
    const status = error.response?.status ?? 0;
    const url = error.config?.url ?? 'unknown';

    // No answer at all (timeout, dropped connection): the request may have
    // gone through, so the key is kept and a retry is recognised. An answer
    // that is an error means nothing was kept server-side.
    const claimed = (error.config as IdempotentConfig | undefined)
      ?.idempotencySignature;
    if (claimed) releaseIdempotencyKey(claimed, !error.response);

    Sentry.addBreadcrumb({
      category: 'http',
      message: `${status} ${url}`,
      level: status >= 500 ? 'error' : 'warning',
      data: { status, url, method: error.config?.method },
    });

    // A report that cannot combine currencies was asked for "All branches" on
    // a multi-currency clinic. Tell the shell, which asks for a branch.
    const body = error.response?.data as
      | { errors?: { code?: string; currencies?: string[] } }
      | undefined;
    if (status === 409 && body?.errors?.code === 'CURRENCY_SCOPE_REQUIRED') {
      window.dispatchEvent(
        new CustomEvent('dc:currency-scope-required', {
          detail: { currencies: body.errors.currencies ?? [], url },
        }),
      );
    }

    // A lapsed clinic is read-only: the API refused a change. Show the
    // reason in the user's language (callers toast the server message), and
    // tell the shell, whose read-only banner may not know yet.
    const refusal = error.response?.data as
      | { message?: unknown; errors?: { code?: string } }
      | undefined;
    if (status === 403 && refusal?.errors?.code === 'SUBSCRIPTION_EXPIRED') {
      refusal.message = i18n.t('subscription.readOnly.refused');
      window.dispatchEvent(new CustomEvent('dc:subscription-read-only'));
    }
    // A feature the plan does not include: name it, and let the shell open
    // the upgrade prompt for it (PlanUpgradeHost), wherever the write came from.
    if (status === 403 && refusal?.errors?.code === 'FEATURE_NOT_IN_PLAN') {
      const feature = (refusal.errors as { feature?: string }).feature;
      if (feature) {
        refusal.message = i18n.t('planFeatures.refused', {
          feature: i18n.t(`planFeatures.items.${feature}.name`),
        });
        window.dispatchEvent(
          new CustomEvent('dc:plan-feature-locked', { detail: { feature } }),
        );
      }
    }

    // Capture server errors as Sentry exceptions; client errors are expected.
    // request_id is the API's own id for the request: the same tag is on the
    // API's Sentry event and on its log lines, so one search finds the stack.
    if (status >= 500) {
      const requestId = (error.response?.data as { requestId?: string })
        ?.requestId;
      Sentry.captureException(error, {
        tags: {
          api_url: url,
          http_status: String(status),
          ...(requestId ? { request_id: requestId } : {}),
        },
      });
    }

    return Promise.reject(error);
  },
);

// ============================================================================
// Token Refresh Logic
// ============================================================================

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: Error | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

const isOnPublicPage = (): boolean => {
  return PUBLIC_PAGES.some((page) => window.location.pathname.includes(page));
};

const forceLogout = () => {
  // Always clear local session state, public page or not. The refresh token was
  // definitively rejected, so the session is over — and leaving the flag behind
  // would make every later page load re-run the auth probe and fail again.
  clearSessionStorage();

  // Tell the backend to clear HTTP-only auth cookies (fire-and-forget)
  axios
    .post(`${API_BASE_URL}/auth/logout`, null, { withCredentials: true })
    .catch(() => {
      /* ignore – logging out regardless */
    });

  // On a public page there is nothing to redirect to, and a hard navigation
  // would reload the page out from under the login form that's already showing.
  if (isOnPublicPage()) return;

  window.location.href = '/login';
};

// ============================================================================
// Response Interceptor
// ============================================================================

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
      _visitConfirmAsked?: Record<string, boolean>;
    };
    const refusal = error.response?.data as
      | { errors?: { code?: string } }
      | undefined;

    // A visit rule staff may knowingly override (a visit booked for a later
    // day, procedures left unfinished). Ask once per rule; on yes, repeat the
    // request with that rule's override. One request can meet both in turn.
    const visitCode = refusal?.errors?.code;
    if (
      error.response?.status === 409 &&
      isVisitConfirmCode(visitCode) &&
      originalRequest &&
      !originalRequest._visitConfirmAsked?.[visitCode]
    ) {
      originalRequest._visitConfirmAsked = {
        ...originalRequest._visitConfirmAsked,
        [visitCode]: true,
      };
      const details = refusal?.errors as unknown as VisitRefusal;
      if (await askVisitConfirm({ ...details, code: visitCode })) {
        // Reschedule takes its answers in the query string.
        if (originalRequest.url?.includes('/reschedule')) {
          originalRequest.params = {
            ...originalRequest.params,
            [VISIT_CONFIRM_OVERRIDES[visitCode]]: true,
          };
          return apiClient(originalRequest);
        }
        const body =
          typeof originalRequest.data === 'string'
            ? JSON.parse(originalRequest.data || '{}')
            : (originalRequest.data ?? {});
        originalRequest.data = {
          ...body,
          [VISIT_CONFIRM_OVERRIDES[visitCode]]: true,
        };
        return apiClient(originalRequest);
      }
    }

    // A visit change the API takes only with a reason (a finished visit moved
    // to another doctor). Ask for it once; repeat the request with it.
    if (
      error.response?.status === 409 &&
      isVisitReasonCode(visitCode) &&
      originalRequest &&
      !originalRequest._visitConfirmAsked?.[visitCode]
    ) {
      originalRequest._visitConfirmAsked = {
        ...originalRequest._visitConfirmAsked,
        [visitCode]: true,
      };
      const reason = await askVisitReason(visitCode);
      if (reason) {
        const body =
          typeof originalRequest.data === 'string'
            ? JSON.parse(originalRequest.data || '{}')
            : (originalRequest.data ?? {});
        originalRequest.data = {
          ...body,
          [VISIT_REASON_FIELDS[visitCode]]: reason,
        };
        return apiClient(originalRequest);
      }
    }

    // Don't retry if this is the refresh token endpoint itself
    if (originalRequest.url?.includes('/auth/refresh-token')) {
      isRefreshing = false;
      processQueue(error as Error);
      // Only force logout on definitive auth failures (401/403), not transient server/network errors
      const refreshStatus = error.response?.status;
      if (refreshStatus === 401 || refreshStatus === 403) {
        forceLogout();
      }
      return Promise.reject(error);
    }

    // _skipRefresh is set by checkAuth — those calls must not trigger refresh to avoid loops
    const skipRefresh = !!(
      originalRequest as InternalAxiosRequestConfig & { _skipRefresh?: boolean }
    )._skipRefresh;

    // Public pages normally don't refresh, but the auth probe opts in: the access
    // token only lives 30m while the refresh token lives 7d, so a returning user
    // landing on /login almost always needs a refresh to be recognised as logged
    // in. Only the probe sets this — a failed sign-in POST still surfaces its own
    // 401 (and its "invalid credentials" message) untouched.
    const allowRefreshOnPublic = !!(
      originalRequest as InternalAxiosRequestConfig & {
        _allowRefreshOnPublic?: boolean;
      }
    )._allowRefreshOnPublic;

    // If error is 401 and we haven't retried yet (skip refresh on public pages or checkAuth calls)
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      (!isOnPublicPage() || allowRefreshOnPublic) &&
      !skipRefresh
    ) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => apiClient(originalRequest))
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Attempt to refresh token - cookies are sent automatically
        await axios.get(`${API_BASE_URL}/auth/refresh-token`, {
          withCredentials: true,
        });

        // New tokens are set in cookies by backend
        processQueue();
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as Error);
        // Only force logout on definitive auth failures, not transient server/network errors
        const refreshStatus = (refreshError as AxiosError).response?.status;
        if (refreshStatus === 401 || refreshStatus === 403) {
          forceLogout();
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

// ============================================================================
// Type-Safe API Methods
// ============================================================================

/**
 * Extract data from Axios response and convert errors to ApiError
 */
async function request<T>(config: AxiosRequestConfig): Promise<T> {
  try {
    const response: AxiosResponse<T> = await apiClient(config);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<{
        message?: string;
        error?: string;
        errors?: Record<string, string[]>;
        code?: string;
      }>;
      throw new ApiError({
        message:
          axiosError.response?.data?.message ||
          axiosError.response?.data?.error ||
          axiosError.message ||
          i18n.t('errors.generic'),
        code: axiosError.response?.data?.code || axiosError.code,
        statusCode: axiosError.response?.status || 0,
        errors: axiosError.response?.data?.errors,
      });
    }
    throw new ApiError({
      message:
        error instanceof Error ? error.message : 'An unexpected error occurred',
      statusCode: 500,
      code: 'UNKNOWN_ERROR',
    });
  }
}

export const api = {
  /**
   * GET request
   */
  get: <T>(
    endpoint: string,
    config?: Omit<AxiosRequestConfig, 'method' | 'url'>,
  ) => request<T>({ ...config, method: 'GET', url: endpoint }),

  /**
   * POST request
   */
  post: <T>(
    endpoint: string,
    data?: unknown,
    config?: Omit<AxiosRequestConfig, 'method' | 'url' | 'data'>,
  ) => request<T>({ ...config, method: 'POST', url: endpoint, data }),

  /**
   * PUT request
   */
  put: <T>(
    endpoint: string,
    data?: unknown,
    config?: Omit<AxiosRequestConfig, 'method' | 'url' | 'data'>,
  ) => request<T>({ ...config, method: 'PUT', url: endpoint, data }),

  /**
   * PATCH request
   */
  patch: <T>(
    endpoint: string,
    data?: unknown,
    config?: Omit<AxiosRequestConfig, 'method' | 'url' | 'data'>,
  ) => request<T>({ ...config, method: 'PATCH', url: endpoint, data }),

  /**
   * DELETE request
   */
  delete: <T>(
    endpoint: string,
    config?: Omit<AxiosRequestConfig, 'method' | 'url'>,
  ) => request<T>({ ...config, method: 'DELETE', url: endpoint }),
};

// ============================================================================
// Type Guards & Utilities
// ============================================================================

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError || (error as ApiError)?.isApiError === true;
}

export function getErrorMessage(error: unknown, fallback?: string): string {
  // class-validator returns `message` as a string[]; surface the first.
  const fromData = (data: unknown): string | undefined => {
    const m = (data as { message?: unknown })?.message;
    if (Array.isArray(m)) return m[0];
    if (typeof m === 'string') return m;
    return undefined;
  };
  if (isApiError(error)) {
    return error.message || fallback || 'An unexpected error occurred';
  }
  if (axios.isAxiosError(error)) {
    return (
      fromData(error.response?.data) ||
      fallback ||
      error.message ||
      'An unexpected error occurred'
    );
  }
  if (error instanceof Error) {
    return fallback || error.message;
  }
  return fallback || 'An unexpected error occurred';
}

/**
 * The HTTP status behind an error, whichever shape it arrived in: the `api`
 * helper throws `ApiError` (`statusCode`), a raw axios call rejects with an
 * `AxiosError` (`response.status`). Reading only one of the two silently
 * never matches for the other.
 */
export function getErrorStatus(error: unknown): number | undefined {
  if (isApiError(error)) return error.statusCode;
  if (axios.isAxiosError(error)) return error.response?.status;
  return undefined;
}

/**
 * Get a user-friendly message for permission-related errors
 */
export function getPermissionErrorMessage(error: unknown): string {
  if (isApiError(error) && error.isForbiddenError()) {
    return 'You do not have permission to perform this action. Please contact your administrator if you believe this is an error.';
  }
  return getErrorMessage(error);
}

/**
 * Check if an error is a permission/forbidden error
 */
export function isForbiddenError(error: unknown): boolean {
  if (isApiError(error)) {
    return error.isForbiddenError();
  }
  if (axios.isAxiosError(error)) {
    return error.response?.status === 403;
  }
  return false;
}

// ============================================================================
// Re-export axios types for convenience
// ============================================================================

export type { AxiosRequestConfig, AxiosResponse, AxiosError };
